import type { OrganizationProfile } from '../../store/organizationStore';

/**
 * The company's time zone on the admin screens (PLAN-GENERICITE.md § 4 quinquies, step 7, R10): the
 * day boundaries, the one-click times and every date shown to a person are computed in the zone of the
 * record (`organizations.timezone`, read through `useOrganization()`), whatever the browser's zone.
 * The Edge Functions read the same record (`supabase/functions/_shared/organizationTimezone.ts`), the
 * SQL readers `organization_timezone()`.
 *
 * The pure helpers of `src/lib` take the zone as a parameter; a screen gets it from
 * `useOrganizationTimeZone()` (`src/hooks/useOrganization.ts`), which starts the read and re-renders
 * when the record arrives. Without the record (loading, none, unreadable, or a zone the browser does
 * not know) the platform default applies: a date is never left out for want of a zone.
 */

/** The platform default: the zone used while the record is loading, absent, unreadable or unknown here. */
export const PLATFORM_TIME_ZONE = 'Europe/Paris';

const known = new Map<string, boolean>();

/** True when the browser's Intl knows this IANA zone (cached: the answer never changes). */
export function isTimeZone(zone: string): boolean {
  let answer = known.get(zone);
  if (answer === undefined) {
    try {
      new Intl.DateTimeFormat('en-US', { timeZone: zone });
      answer = true;
    } catch {
      answer = false;
    }
    known.set(zone, answer);
  }
  return answer;
}

/** The record's zone when it is one the browser knows, else the platform default. Pure. */
export function timeZoneOf(organization: Pick<OrganizationProfile, 'timezone'> | null): string {
  const zone = organization?.timezone.trim() ?? '';
  return zone !== '' && isTimeZone(zone) ? zone : PLATFORM_TIME_ZONE;
}

/** « Paris », « Reunion », « New York »: the place an IANA zone is named after, for a label. Pure. */
export function timeZoneCity(zone: string): string {
  return (zone.split('/').pop() ?? zone).replace(/_/g, ' ');
}

/** `YYYY-MM-DD` of the calendar day containing `instant` in `timeZone`. Pure. */
export function dayInZone(instant: Date | number, timeZone: string): string {
  return new Intl.DateTimeFormat('fr-CA', { timeZone, year: 'numeric', month: '2-digit', day: '2-digit' }).format(instant);
}

/** One value per zone, built on first use: for an `Intl.DateTimeFormat` a hot list reuses. */
export function perZone<T>(make: (timeZone: string) => T): (timeZone: string) => T {
  const cache = new Map<string, T>();
  return (timeZone) => {
    let value = cache.get(timeZone);
    if (value === undefined) {
      value = make(timeZone);
      cache.set(timeZone, value);
    }
    return value;
  };
}
